import { useEffect, useState } from 'react'

import api from '@disco/api/server'

import type { Track } from 'types/track'

const SEARCH_DEBOUNCE_MS = 300

/**
 * Searches audio tracks in the given inboxes, newest first until a query is typed.
 * @param inboxIds - Group ids of the inboxes to search; must be referentially stable
 * @param query - Keyword query; empty lists the most recent tracks
 */
export function useInboxTracks(inboxIds: number[], query: string) {
  const [tracks, setTracks] = useState<Track[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (inboxIds.length === 0) {
      setTracks([])
      setLoading(false)
      return
    }

    const abortController = new AbortController()
    setLoading(true)
    const timeout = setTimeout(
      async () => {
        try {
          const response = await api.getSearchResultsByQuery(
            inboxIds,
            {
              sort: query ? '["-score"]' : '["-created"]',
              type: 'track',
              track_type: 'audio',
              search_folders: true,
              include_playlist_tracks: true,
              q: query,
              count: 20,
            },
            undefined,
            abortController.signal,
          )
          setTracks(response.results ?? [])
          setLoading(false)
        } catch {
          if (!abortController.signal.aborted) {
            setTracks([])
            setLoading(false)
          }
        }
      },
      query ? SEARCH_DEBOUNCE_MS : 0,
    )

    return () => {
      clearTimeout(timeout)
      abortController.abort()
    }
  }, [inboxIds, query])

  return { tracks, loading }
}
